/**
 * Journal et phases d'un rendez-vous, au format de la relecture des sessions :
 * une ligne datée par fait (événement, entrée ou sortie de zone, début et fin
 * d'enregistrement, photo), et les segments de couleur de la timeline.
 */

export const RDV_PHASE_COLORS = Object.freeze({
    route: '#00BCD4', // en route
    zone: '#FFD60A', // au moins une personne dans la zone
    met: '#22C55E', // rencontre confirmée
    over: '#64748B', // terminé
    alert: '#EF4444',
});

const EVENT_COLORS = { met: RDV_PHASE_COLORS.met, wellbeing: RDV_PHASE_COLORS.met, completed: RDV_PHASE_COLORS.over,
    consent: '#A855F7', 'alert-start': RDV_PHASE_COLORS.alert, 'alert-end': RDV_PHASE_COLORS.alert,
    'alert-journal': RDV_PHASE_COLORS.alert };

const firstEventTime = (bundle, kind) => {
    const event = (bundle?.events || []).find(candidate => candidate.kind === kind);
    return event ? event.t : null;
};

/** Premier instant où une personne est dans la zone, ou null. */
export function firstInZoneTime(bundle) {
    const times = (bundle?.tracks || [])
        .map(track => (track.positions || []).find(position => position.inZone === true)?.t)
        .filter(Number.isFinite);
    return times.length ? Math.min(...times) : null;
}

/** Segments { startTs, endTs, color } de la timeline : en route, dans la zone, rencontre, terminé. */
export function rendezVousPhases(bundle) {
    const start = bundle?.startMs;
    const end = bundle?.endMs;
    if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
    const marks = [
        { t: start, color: RDV_PHASE_COLORS.route },
        { t: firstInZoneTime(bundle), color: RDV_PHASE_COLORS.zone },
        { t: firstEventTime(bundle, 'met'), color: RDV_PHASE_COLORS.met },
        { t: firstEventTime(bundle, 'completed'), color: RDV_PHASE_COLORS.over },
    ].filter(mark => Number.isFinite(mark.t) && mark.t >= start && mark.t <= end)
        .sort((a, b) => a.t - b.t);
    const base = marks.map((mark, index) => ({
        startTs: mark.t,
        endTs: index + 1 < marks.length ? marks[index + 1].t : end,
        color: mark.color,
    })).filter(segment => segment.endTs > segment.startTs || marks.length === 1);
    const alerts = (bundle.alerts || []).filter(alert => Number.isFinite(alert.startMs));
    if (!alerts.length) return base;
    const cuts = [...new Set([start, end, ...base.flatMap(segment => [segment.startTs, segment.endTs]),
        ...alerts.flatMap(alert => [Math.max(start, alert.startMs), Math.min(end, alert.endMs ?? end)])])]
        .filter(t => t >= start && t <= end).sort((a, b) => a - b);
    return cuts.slice(0, -1).map((from, index) => {
        const to = cuts[index + 1];
        const active = alerts.some(alert => alert.startMs < to && (alert.endMs ?? end) > from);
        return { startTs: from, endTs: to,
            color: active ? RDV_PHASE_COLORS.alert : base.find(segment => segment.startTs <= from && segment.endTs >= to)?.color || RDV_PHASE_COLORS.route };
    }).filter(segment => segment.endTs > segment.startTs);
}

/** Dégradé CSS des phases, comme la timeline des sessions. */
export function phasesGradient(phases, start, end) {
    const total = end - start;
    if (!phases.length || !(total > 0)) return `linear-gradient(to right, ${RDV_PHASE_COLORS.route} 0%, ${RDV_PHASE_COLORS.route} 100%)`;
    const stops = phases.flatMap(phase => {
        const from = (((phase.startTs - start) / total) * 100).toFixed(2);
        const to = (((phase.endTs - start) / total) * 100).toFixed(2);
        return [`${phase.color} ${from}%`, `${phase.color} ${to}%`];
    });
    return `linear-gradient(to right, ${stops.join(', ')})`;
}

const MEDIA_WORDS = { video: 'vidéo', audio: 'audio' };

/** Lignes du journal { t, color, text, trackId, kind }, dans l'ordre chronologique. */
export function buildRendezVousJournal(bundle) {
    if (!bundle) return [];
    const entries = [];
    const trackOf = id => (bundle.tracks || []).find(track => track.id === id) || null;
    (bundle.events || []).forEach(event => {
        if (event.kind === 'photo') return; // ligne dédiée ci-dessous, avec l'auteur et le motif
        const track = trackOf(event.trackId);
        entries.push({ t: event.t, kind: event.kind, trackId: event.trackId, text: event.label,
            color: EVENT_COLORS[event.kind] || track?.color || RDV_PHASE_COLORS.route });
    });
    (bundle.tracks || []).forEach(track => {
        const positions = track.positions || [];
        if (positions.length) {
            entries.push({ t: positions[0].t, kind: 'position', trackId: track.id, color: track.color,
                text: `${track.label} : première position${positions[0].accuracy ? ` (±${Math.round(positions[0].accuracy)} m)` : ''}` });
        }
        let inside = null;
        positions.forEach(position => {
            if (typeof position.inZone !== 'boolean' || position.inZone === inside) return;
            if (inside !== null || position.inZone) {
                entries.push({ t: position.t, kind: position.inZone ? 'zone-in' : 'zone-out', trackId: track.id, color: track.color,
                    text: `${track.label} ${position.inZone ? 'entre dans la zone' : 'sort de la zone'}` });
            }
            inside = position.inZone;
        });
        if (positions.length > 1) {
            const last = positions[positions.length - 1];
            entries.push({ t: last.t, kind: 'position', trackId: track.id, color: track.color, text: `${track.label} : dernière position reçue` });
        }
        (track.media || []).forEach(media => {
            const word = MEDIA_WORDS[media.type] || media.type;
            if (Number.isFinite(media.startMs)) {
                entries.push({ t: media.startMs, kind: 'media-start', trackId: track.id, color: track.color,
                    text: `Début de l’enregistrement ${word} de ${track.label}` });
            }
            if (Number.isFinite(media.endMs) && media.endMs > media.startMs) {
                entries.push({ t: media.endMs, kind: 'media-end', trackId: track.id, color: track.color,
                    text: `Fin de l’enregistrement ${word} de ${track.label} (${media.segmentCount || 0} segment(s))` });
            }
        });
        (track.photos || []).forEach(photo => entries.push({ t: photo.t, kind: photo.purpose === 'arrival' ? 'arrival-photo' : 'photo', trackId: track.id, color: track.color,
            photoId: photo.id, text: photo.purpose === 'arrival' ? `Photo de la personne à l’arrivée, prise par ${track.label}` : `Photo prise par ${track.label}` }));
    });
    return entries.filter(entry => Number.isFinite(entry.t)).sort((a, b) => a.t - b.t);
}
